// Set de íconos de nav — SVG 12px inline. Todos pintan por `currentColor`, así que
// el color lo pone quien los usa (p. ej. text-accent en activo, text-neutral-dot en inactivo).
// Escalables por `size` (default 12); className passthrough.
import type { ReactNode } from "react";
import { cn } from "./cn";

type IconProps = { size?: number; className?: string };

// Envoltura común: viewBox 0 0 12 12, color heredado por currentColor.
function Icon({ size = 12, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="currentColor"
      aria-hidden="true"
      className={cn("flex-none", className)}
    >
      {children}
    </svg>
  );
}

// Hoy — círculo simple.
export function IconHoy(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="6" cy="6" r="5" />
    </Icon>
  );
}

// Casos — rombo (rect girado 45°).
export function IconCasos(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="6" height="6" rx="1.2" transform="rotate(45 6 6)" />
    </Icon>
  );
}

// Ventas — tres barras verticales 5/9/7.
export function IconVentas(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="1" y="7" width="2" height="5" rx="0.5" />
      <rect x="5" y="3" width="2" height="9" rx="0.5" />
      <rect x="9" y="5" width="2" height="7" rx="0.5" />
    </Icon>
  );
}

// Inventario — dos cajones apilados.
export function IconInventario(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="1.5" y="2.2" width="9" height="3.1" rx="1" />
      <rect x="1.5" y="6.7" width="9" height="3.1" rx="1" />
    </Icon>
  );
}

// Compras — dos círculos superpuestos (el segundo, atenuado).
export function IconCompras(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="4.6" cy="6" r="3.4" />
      <circle cx="8.4" cy="7.2" r="2.6" opacity="0.55" />
    </Icon>
  );
}

// Ajustes — anillo atenuado + núcleo.
export function IconAjustes(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="6" cy="6" r="5" opacity="0.35" />
      <circle cx="6" cy="6" r="2.4" />
    </Icon>
  );
}

// Oído (audio) — equalizer de tres barras 4/8/3.
export function IconOido(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="1" y="8" width="2" height="4" rx="0.5" />
      <rect x="5" y="4" width="2" height="8" rx="0.5" />
      <rect x="9" y="9" width="2" height="3" rx="0.5" />
    </Icon>
  );
}

// Mapa — plano plegado en tres paneles.
export function IconMapa(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M1 3 L4 2 L4 10 L1 11 Z" />
      <path d="M4.7 2 L7.3 3 L7.3 11 L4.7 10 Z" />
      <path d="M8 3 L11 2 L11 10 L8 11 Z" />
    </Icon>
  );
}

// Aprendizaje — libro abierto (las fichas y el examen del día; brief 26-oct-2026).
export function IconAprendizaje(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3 C4.5 2 2.5 2 1 2.5 L1 10 C2.5 9.5 4.5 9.5 6 10.5 Z" />
      <path d="M6 3 C7.5 2 9.5 2 11 2.5 L11 10 C9.5 9.5 7.5 9.5 6 10.5 Z" />
    </Icon>
  );
}

// Clientes — cabeza y hombros (una persona, que es de lo que se trata el padrón).
export function IconClientes(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="6" cy="3.6" r="2.4" />
      <path d="M1.6 11 C1.6 8.4 3.6 6.9 6 6.9 C8.4 6.9 10.4 8.4 10.4 11 Z" />
    </Icon>
  );
}

// Mostrador (POS) — toldo + cuerpo de tienda.
export function IconMostrador(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="1" y="1.5" width="10" height="2.5" rx="1" />
      <rect x="2" y="5.5" width="8" height="5.5" rx="1" />
    </Icon>
  );
}

// Catálogo — etiqueta de precio con su ojal (sección propia desde el tramo 03).
export function IconCatalogo(props: IconProps) {
  return (
    <Icon {...props}>
      <path fillRule="evenodd" d="M1.2 2.4H7l3.8 3.6L7 9.6H1.2Z M3.6 5a1 1 0 1 0 0.001 0Z" />
    </Icon>
  );
}

// Recepción — caja con la tapa atenuada (la mercadería que llega).
export function IconRecepcion(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="1" y="1.6" width="10" height="2.2" rx="0.8" opacity="0.55" />
      <rect x="1.8" y="4.6" width="8.4" height="6" rx="1" />
    </Icon>
  );
}

// Avisos — globo de mensaje (a quién hay que escribirle). Antes repetía el ícono de Clientes.
export function IconAvisos(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.2 1.6h7.6c.7 0 1.2.5 1.2 1.2v4.6c0 .7-.5 1.2-1.2 1.2H5.4L2.8 10.6V8.6h-.6C1.5 8.6 1 8.1 1 7.4V2.8c0-.7.5-1.2 1.2-1.2Z" />
    </Icon>
  );
}

// Sugerencias — foco (el consejo del mostrador): bulbo y casquillo.
export function IconSugerencias(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="6" cy="4.6" r="3.6" />
      <rect x="4.2" y="8.6" width="3.6" height="2.4" rx=".6" />
    </Icon>
  );
}
